/**
 * HOF-696 D18 — "Mein Arbeitstag": the worker reports the day on the phone.
 *
 * Today (entries + total) → "+ Tätigkeit" (activity → crop/area → duration) →
 * "Wie gestern" / "Tag abschließen" with the optional reflection; a day switcher
 * over the 8 editable dates; "Meine Woche"; for a Vorarbeiter (HOF-697) a third
 * tab, "Team", with the team week the route hands in. Built on the Morgenlage mobile shell
 * (the design system's `mgl-root` / `mgl-screen` / `mgl-card` classes), the
 * prototype's *Mein Arbeitstag* look.
 *
 * All data goes through `useLabour` (the one data hook). Dates come from the
 * injected `now` read in Europe/Berlin (`labourDates.ts`), never `utils/today.ts`.
 * The unsent form lives in a local draft with its client id (`labourDraft.ts`).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RadioCardGroup } from '@hofsync/design-system';
import {
  catalogueLabel,
  classifyLabourError,
  isAccessRefusal,
  useLabourCatalogue,
  useLabourCommands,
  useMyLabourDays,
  type CatalogueCostObject,
  type LabourEntry,
  type LabourErrorKind,
} from '../../../hooks/useLabour';
import { berlinDate, editableDates, formatDayLabel, isoWeekDates } from './labourDates';
import {
  DURATION_STEP,
  clampDuration,
  clearDraft,
  loadDraft,
  newDraft,
  saveDraft,
  type LabourDraft,
} from './labourDraft';
import { formatMinutes } from './labourFormat';
import LabourBlocked, { type LabourBlockReason } from './LabourBlocked';
import './Labour.css';

type Screen = 'today' | 'add' | 'confirm' | 'week' | 'team';
type Toast = { text: string; tone: 'ok' | 'bad' };

const QUICK_HOURS = [1, 2, 4, 8];
const TEXT_MAX = 500;
const TOAST_MS = 3200;

export interface WorkerAppProps {
  /** The clock, injectable for tests; read in Europe/Berlin. */
  now?: () => Date;
  /** Scopes the local draft to one farm × user, so a shared phone never mixes them. */
  draftScope?: string;
  /** The seat's twin person (`active_farm.person_id`): "my day" is read by it (HOF-697). */
  personId?: string | null;
  /**
   * HOF-697: the team week a seat that may read it (`labour.read`, a Vorarbeiter) sees under a
   * third tab, "Team". Absent for a worker — no tab.
   */
  teamView?: React.ReactNode;
}

const defaultNow = () => new Date();

const WorkerApp: React.FC<WorkerAppProps> = ({ now = defaultNow, draftScope = 'default', personId = null, teamView }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'de';

  const [today, setToday] = useState(() => berlinDate(now()));
  const dates = useMemo(() => editableDates(today), [today]);

  // A restored draft reopens the form on its own day (if that day is still editable).
  const [draft, setDraft] = useState<LabourDraft | null>(() => {
    const restored = loadDraft(draftScope);
    if (restored && !editableDates(berlinDate(now())).includes(restored.workDay)) {
      clearDraft(draftScope);
      return null;
    }
    return restored;
  });
  const [day, setDay] = useState(() => draft?.workDay ?? today);
  const [screen, setScreen] = useState<Screen>(() => (draft ? 'add' : 'today'));
  const [blocked, setBlocked] = useState<LabourBlockReason | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [reflection, setReflection] = useState('');
  // Whether the worker edited the reflection field: an untouched field is omitted (the stored one
  // stays), a touched one is sent as typed — "" included, which clears it (codex r1 #5).
  const [reflectionTouched, setReflectionTouched] = useState(false);

  // Midnight rollover: re-read the Berlin date every minute and on focus.
  useEffect(() => {
    const tick = () => setToday(berlinDate(now()));
    const id = window.setInterval(tick, 60_000);
    window.addEventListener('focus', tick);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', tick);
    };
  }, [now]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(id);
  }, [toast]);

  const catalogue = useLabourCatalogue();
  const days = useMyLabourDays(dates, true, personId);
  const { putEntry, deleteEntry, copyPrevious, confirmDay } = useLabourCommands();

  // Persist every change of the unsent form.
  const updateDraft = useCallback(
    (next: LabourDraft | null) => {
      setDraft(next);
      if (next) saveDraft(draftScope, next);
      else clearDraft(draftScope);
    },
    [draftScope],
  );

  const readError = days.error ?? catalogue.error;
  useEffect(() => {
    if (!readError) return;
    const kind = classifyLabourError(readError);
    if (isAccessRefusal(kind)) setBlocked(kind as LabourBlockReason);
    else if (kind === 'forbidden') setBlocked('access_ended');
  }, [readError]);

  const showCommandError = useCallback(
    (kind: LabourErrorKind) => {
      if (isAccessRefusal(kind)) {
        setBlocked(kind as LabourBlockReason);
        return;
      }
      const key: Record<LabourErrorKind, string> = {
        access_ended: 'failed',
        app_not_active: 'failed',
        forbidden: 'failed',
        busy: 'busy',
        gone: 'gone',
        day_cap: 'dayCap',
        not_employed: 'failed',
        invalid: 'invalid',
        unavailable: 'busy',
        network: 'offline',
        unknown: 'failed',
      };
      setToast({ text: t(`labour.toast.${key[kind]}`), tone: 'bad' });
    },
    [t],
  );

  const activities = catalogue.data?.activities ?? [];
  const costObjects = catalogue.data?.costObjects ?? [];
  const activityLabel = (key: string) => catalogueLabel(activities.find((a) => a.key === key), lang) || key;
  const costLabel = (key: string) => catalogueLabel(costObjects.find((c) => c.key === key), lang) || key;

  const dayEntries = useMemo(
    () => (days.data?.entries ?? []).filter((e) => e.workDay === day),
    [days.data, day],
  );
  const minutesOn = (d: string) =>
    (days.data?.entries ?? []).filter((e) => e.workDay === d).reduce((sum, e) => sum + e.durationMin, 0);
  const reportOn = (d: string) => (days.data?.reports ?? []).find((r) => r.workDay === d);
  const dayReport = reportOn(day);

  if (blocked) return <LabourBlocked reason={blocked} />;

  const longDay = (d: string) => formatDayLabel(d, lang, { weekday: 'long', day: 'numeric', month: 'long' });
  const dayIndex = dates.indexOf(day);

  // ------------------------------------------------------------- actions

  const startNew = () => {
    updateDraft(newDraft(day));
    setScreen('add');
  };

  const startEdit = (entry: LabourEntry) => {
    if (!entry.clientId) return;
    updateDraft({
      clientId: entry.clientId,
      workDay: entry.workDay,
      step: 2,
      activityKey: entry.activityKey,
      costObjectKey: entry.costObjectKey,
      durationMin: clampDuration(entry.durationMin),
      note: entry.note,
      isEdit: true,
      status: 'editing',
    });
    setScreen('add');
  };

  const leaveForm = () => {
    updateDraft(null);
    setScreen('today');
  };

  const save = () => {
    if (!draft?.activityKey || !draft.costObjectKey) return;
    const sending = draft;
    putEntry.mutate(
      {
        clientId: sending.clientId,
        input: {
          workDay: sending.workDay,
          activityKey: sending.activityKey!,
          costObjectKey: sending.costObjectKey!,
          durationMin: sending.durationMin,
          note: sending.note,
        },
      },
      {
        onSuccess: () => {
          updateDraft(null);
          setDay(sending.workDay);
          setScreen('today');
          setToast({ text: t('labour.toast.saved'), tone: 'ok' });
        },
        onError: (error) => {
          const kind = classifyLabourError(error);
          if (kind === 'gone') {
            updateDraft(null);
            setScreen('today');
          } else if (kind === 'network' || kind === 'busy' || kind === 'unavailable') {
            // Kept on the phone with its client id; the retry is the same idempotent PUT.
            updateDraft({ ...sending, status: 'failed' });
          }
          showCommandError(kind);
        },
      },
    );
  };

  const remove = (entry: LabourEntry) => {
    if (!entry.clientId) return;
    deleteEntry.mutate(
      { clientId: entry.clientId },
      {
        onSuccess: () => setToast({ text: t('labour.toast.deleted'), tone: 'ok' }),
        onError: (error) => showCommandError(classifyLabourError(error)),
      },
    );
  };

  const copy = () => {
    copyPrevious.mutate(
      { day },
      {
        onSuccess: (result) => {
          const text =
            result.copied === 0
              ? t('labour.toast.copiedNothing')
              : result.sourceDay
                ? t('labour.toast.copied', { day: longDay(result.sourceDay) })
                : t('labour.toast.saved');
          setToast({ text, tone: 'ok' });
        },
        onError: (error) => showCommandError(classifyLabourError(error)),
      },
    );
  };

  const openConfirm = () => {
    setReflection(dayReport?.reflection ?? '');
    setReflectionTouched(false);
    setScreen('confirm');
  };

  const confirm = () => {
    confirmDay.mutate(
      { day, reflection: reflectionTouched ? reflection : undefined },
      {
        onSuccess: () => {
          setScreen('today');
          setToast({ text: t('labour.toast.confirmed'), tone: 'ok' });
        },
        onError: (error) => showCommandError(classifyLabourError(error)),
      },
    );
  };

  // ------------------------------------------------------------- screens

  const renderAdd = (current: LabourDraft) => {
    const back = () => {
      // An edit re-picking its activity/place goes back to the edit, not to the day.
      if (current.isEdit && current.step < 2) updateDraft({ ...current, step: 2 });
      else if (current.step === 0 || current.isEdit) leaveForm();
      else updateDraft({ ...current, step: (current.step - 1) as 0 | 1 });
    };
    const crops = costObjects.filter((c) => c.kind === 'crop');
    const areas = costObjects.filter((c) => c.kind === 'area');
    const costOptions = (items: CatalogueCostObject[]) => items.map((c) => ({ value: c.key, label: catalogueLabel(c, lang) }));
    const pickCost = (key: string) => updateDraft({ ...current, costObjectKey: key, step: 2 });

    return (
      <>
        <div className="lab-formhead">
          <button type="button" className="mgl-btn mgl-btn--ghost lab-back" onClick={back}>
            <span aria-hidden="true">‹ </span>
            {t('labour.add.back')}
          </button>
          <span className="lab-steps" aria-label={t('labour.add.step', { step: current.step + 1 })}>
            {[0, 1, 2].map((i) => (
              <i key={i} className={i <= current.step ? 'lab-steps__on' : undefined} />
            ))}
          </span>
        </div>
        <h2 className="lab-question">
          {current.isEdit ? t('labour.add.titleEdit') : t('labour.add.titleNew')} · {longDay(current.workDay)}
        </h2>

        {catalogue.isSuccess && activities.length === 0 ? (
          <p className="mgl-card lab-muted">{t('labour.noCatalogue')}</p>
        ) : current.step === 0 ? (
          <RadioCardGroup
            legend={t('labour.add.what')}
            name="lab-activity"
            value={current.activityKey ?? ''}
            onValueChange={(key) => updateDraft({ ...current, activityKey: key, step: 1 })}
            options={activities.map((a) => ({ value: a.key, label: catalogueLabel(a, lang) }))}
          />
        ) : current.step === 1 ? (
          <div className="lab-costs">
            <p className="lab-question">{t('labour.add.where')}</p>
            {crops.length > 0 && (
              <RadioCardGroup
                legend={t('labour.add.crop')}
                name="lab-crop"
                value={current.costObjectKey ?? ''}
                onValueChange={pickCost}
                options={costOptions(crops)}
              />
            )}
            {areas.length > 0 && (
              <RadioCardGroup
                legend={t('labour.add.area')}
                name="lab-area"
                value={current.costObjectKey ?? ''}
                onValueChange={pickCost}
                options={costOptions(areas)}
              />
            )}
          </div>
        ) : (
          <div className="lab-duration-step">
            <p className="lab-question">{t('labour.add.how')}</p>
            <p className="lab-muted">
              {activityLabel(current.activityKey ?? '')} · {costLabel(current.costObjectKey ?? '')}
            </p>
            {current.isEdit && (
              <button
                type="button"
                className="mgl-btn mgl-btn--ghost lab-change"
                onClick={() => updateDraft({ ...current, step: 0 })}
              >
                {t('labour.add.changeWhat')}
              </button>
            )}
            <div className="lab-dur">
              <button
                type="button"
                className="lab-dur__step"
                aria-label={t('labour.add.less')}
                onClick={() => updateDraft({ ...current, durationMin: clampDuration(current.durationMin - DURATION_STEP) })}
              >
                −15
              </button>
              <b className="lab-dur__value mgl-num" data-testid="lab-duration">
                {formatMinutes(current.durationMin)} h
              </b>
              <button
                type="button"
                className="lab-dur__step"
                aria-label={t('labour.add.more')}
                onClick={() => updateDraft({ ...current, durationMin: clampDuration(current.durationMin + DURATION_STEP) })}
              >
                +15
              </button>
            </div>
            <div className="lab-quick" role="group" aria-label={t('labour.add.quick')}>
              {QUICK_HOURS.map((h) => (
                <button
                  key={h}
                  type="button"
                  className={`mgl-btn${current.durationMin === h * 60 ? ' lab-quick--on' : ''}`}
                  onClick={() => updateDraft({ ...current, durationMin: clampDuration(h * 60) })}
                >
                  {h} h
                </button>
              ))}
            </div>
            <label className="lab-field">
              <span className="lab-field__label">{t('labour.add.note')}</span>
              <textarea
                className="lab-input"
                maxLength={TEXT_MAX}
                rows={2}
                value={current.note}
                onChange={(e) => updateDraft({ ...current, note: e.target.value })}
              />
            </label>
            {current.status === 'failed' && (
              <p className="lab-notsent" role="status">
                {t('labour.add.notSent')}
              </p>
            )}
            <button
              type="button"
              className="mgl-btn mgl-btn--primary lab-big"
              disabled={putEntry.isPending}
              onClick={save}
            >
              {putEntry.isPending
                ? t('labour.add.saving')
                : current.status === 'failed'
                  ? t('labour.add.retry')
                  : t('labour.add.save')}
            </button>
            {current.status === 'failed' && (
              <button type="button" className="mgl-btn mgl-btn--ghost" onClick={leaveForm}>
                {t('labour.add.discard')}
              </button>
            )}
          </div>
        )}
      </>
    );
  };

  const renderToday = () => {
    const total = minutesOn(day);
    return (
      <>
        <div className="lab-dayrow">
          <button
            type="button"
            className="lab-nav"
            aria-label={t('labour.day.previous')}
            disabled={dayIndex <= 0}
            onClick={() => setDay(dates[Math.max(0, dayIndex - 1)])}
          >
            ‹
          </button>
          <div className="lab-dayrow__label">
            <b data-testid="lab-day-label">{longDay(day)}</b>
            {day === today && <span className="lab-muted">{t('labour.day.today')}</span>}
          </div>
          <button
            type="button"
            className="lab-nav"
            aria-label={t('labour.day.next')}
            disabled={day >= today}
            onClick={() => setDay(dates[Math.min(dates.length - 1, dayIndex + 1)])}
          >
            ›
          </button>
        </div>

        <section className="mgl-card lab-total">
          <span>
            {t('labour.day.total')}{' '}
            <span className={`mgl-chip ${dayReport?.confirmed ? 'mgl-chip--up' : 'mgl-chip--soft'}`}>
              {dayReport?.confirmed ? `✓ ${t('labour.day.confirmed')}` : t('labour.day.open')}
            </span>
          </span>
          <b className="mgl-num" data-testid="lab-day-total">
            {formatMinutes(total)} h
          </b>
        </section>

        {days.isLoading ? (
          <p className="lab-muted">{t('labour.loading')}</p>
        ) : days.isError ? (
          <div className="mgl-card">
            <p>{t('labour.loadError')}</p>
            <button type="button" className="mgl-btn" onClick={() => days.refetch()}>
              {t('labour.retry')}
            </button>
          </div>
        ) : dayEntries.length === 0 ? (
          <p className="mgl-card lab-muted">{t('labour.day.empty')}</p>
        ) : (
          <ul className="mgl-card lab-entries">
            {dayEntries.map((entry) => {
              const activity = activityLabel(entry.activityKey);
              return (
                <li key={entry.entityId || entry.clientId} className="lab-entry">
                  <button
                    type="button"
                    className="lab-entry__main"
                    disabled={!entry.clientId}
                    aria-label={t('labour.day.edit', { activity })}
                    onClick={() => startEdit(entry)}
                  >
                    <b>{activity}</b>
                    <span className="lab-muted">
                      {costLabel(entry.costObjectKey)}
                      {entry.note ? ` · ${entry.note}` : ''}
                    </span>
                  </button>
                  <span className="lab-entry__hours mgl-num">{formatMinutes(entry.durationMin)}</span>
                  <button
                    type="button"
                    className="lab-entry__delete"
                    disabled={!entry.clientId || deleteEntry.isPending}
                    aria-label={t('labour.day.delete', { activity })}
                    onClick={() => remove(entry)}
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <button type="button" className="mgl-btn mgl-btn--primary lab-big" onClick={startNew}>
          {t('labour.actions.add')}
        </button>
        <div className="lab-pair">
          <button type="button" className="mgl-btn lab-big" disabled={copyPrevious.isPending} onClick={copy}>
            {t('labour.actions.copy')}
          </button>
          <button type="button" className="mgl-btn lab-big lab-dark" onClick={openConfirm}>
            {t('labour.actions.close')}
          </button>
        </div>
      </>
    );
  };

  const renderConfirm = () => (
    <>
      <div className="lab-formhead">
        <button type="button" className="mgl-btn mgl-btn--ghost lab-back" onClick={() => setScreen('today')}>
          <span aria-hidden="true">‹ </span>
          {t('labour.add.back')}
        </button>
      </div>
      <h2 className="lab-question">
        {t('labour.confirm.title')} · {longDay(day)}
      </h2>
      <section className="mgl-card lab-total">
        <span>{t('labour.day.total')}</span>
        <b className="mgl-num">{formatMinutes(minutesOn(day))} h</b>
      </section>
      <label className="lab-field">
        <span className="lab-field__label">{t('labour.confirm.reflection')}</span>
        <textarea
          className="lab-input"
          maxLength={TEXT_MAX}
          rows={4}
          placeholder={t('labour.confirm.reflectionPlaceholder')}
          value={reflection}
          onChange={(e) => {
            setReflection(e.target.value);
            setReflectionTouched(true);
          }}
        />
      </label>
      <button type="button" className="mgl-btn mgl-btn--primary lab-big" disabled={confirmDay.isPending} onClick={confirm}>
        {t('labour.confirm.submit')}
      </button>
    </>
  );

  const renderWeek = () => {
    const week = isoWeekDates(today);
    const weekTotal = week.filter((d) => d <= today).reduce((sum, d) => sum + minutesOn(d), 0);
    return (
      <>
        <h2 className="lab-question">{t('labour.week.title')}</h2>
        <ul className="mgl-card lab-week">
          {week.map((d) => {
            const future = d > today;
            const minutes = future ? 0 : minutesOn(d);
            const report = reportOn(d);
            const missing = !future && d < today && minutes === 0 && !report?.confirmed;
            const status = future
              ? ''
              : report?.confirmed
                ? `✓ ${t('labour.day.confirmed')}`
                : minutes > 0
                  ? t('labour.day.open')
                  : '';
            return (
              <li key={d} data-testid="lab-week-row" className={`lab-week__row${future ? ' lab-week__row--future' : ''}`}>
                <button
                  type="button"
                  className="lab-week__day"
                  disabled={future || !dates.includes(d)}
                  onClick={() => {
                    setDay(d);
                    setScreen('today');
                  }}
                >
                  <b>{formatDayLabel(d, lang)}</b>
                  {status && <span className="lab-muted">{status}</span>}
                </button>
                {missing && <span className="lab-missing">{t('labour.week.missing')}</span>}
                <b className="mgl-num">{future ? '' : minutes ? formatMinutes(minutes) : '–'}</b>
              </li>
            );
          })}
        </ul>
        <section className="mgl-card lab-total">
          <span>{t('labour.week.total')}</span>
          <b className="mgl-num" data-testid="lab-week-total">
            {formatMinutes(weekTotal)} h
          </b>
        </section>
      </>
    );
  };

  return (
    <div className="lab-worker">
      <h1 className="lab-title">{t('labour.title')}</h1>
      {screen === 'add' && draft
        ? renderAdd(draft)
        : screen === 'confirm'
          ? renderConfirm()
          : screen === 'week'
            ? renderWeek()
            : screen === 'team' && teamView
              ? teamView
              : renderToday()}

      {screen !== 'add' && (
        <nav className="mgl-tseg lab-tabs" aria-label={t('labour.title')}>
          <button
            type="button"
            className={`mgl-tseg-btn${screen !== 'week' && screen !== 'team' ? ' mgl-tseg-btn--active' : ''}`}
            aria-pressed={screen !== 'week' && screen !== 'team'}
            onClick={() => setScreen('today')}
          >
            {t('labour.tabs.today')}
          </button>
          <button
            type="button"
            className={`mgl-tseg-btn${screen === 'week' ? ' mgl-tseg-btn--active' : ''}`}
            aria-pressed={screen === 'week'}
            onClick={() => setScreen('week')}
          >
            {t('labour.tabs.week')}
          </button>
          {teamView && (
            <button
              type="button"
              className={`mgl-tseg-btn${screen === 'team' ? ' mgl-tseg-btn--active' : ''}`}
              aria-pressed={screen === 'team'}
              onClick={() => setScreen('team')}
            >
              {t('labour.tabs.team')}
            </button>
          )}
        </nav>
      )}

      {toast && (
        <p className={`lab-toast${toast.tone === 'bad' ? ' lab-toast--bad' : ''}`} role="status">
          {toast.text}
        </p>
      )}
    </div>
  );
};

export default WorkerApp;
